import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  QuestionAnswer as QuestionIcon
} from '@mui/icons-material';
import axios from 'axios';

const CategoryManager = () => {
  const [categories, setCategories] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentCategory, setCurrentCategory] = useState({
    id: null,
    name: '',
    description: ''
  });

  useEffect(() => {


    // In a real application, we would fetch data like this:

    axios.get(`${process.env.REACT_APP_API_URL}/categories`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    })
      .then(response => {
        if (response.data.success) {
          setCategories(response.data.data);
        }
      })
      .catch(error => {
        console.error('Error fetching categories:', error);
      });

    axios.get(`${process.env.REACT_APP_API_URL}/questions`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    })
      .then(response => {
        if (response.data.success) {
          setQuestions(response.data.data);
        }
      })
      .catch(error => {
        console.error('Error fetching questions:', error);
      });

  }, []);

  const handleOpenDialog = (category = null) => {
    if (category) {
      setCurrentCategory({ ...category });
    } else {
      setCurrentCategory({
        id: null,
        name: '',
        description: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentCategory({ ...currentCategory, [name]: value });
  };

  const handleSaveCategory = () => {
    // In a real application, we would send this data to the backend
    if (currentCategory.id) {
      // Update existing category
      const updatedCategories = categories.map(c =>
        c.id === currentCategory.id ? currentCategory : c
      );
      setCategories(updatedCategories);

      axios.put(`${process.env.REACT_APP_API_URL}/categories/${currentCategory.id}`, currentCategory, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })
        .then(response => {
          // Update categories list
          setCategories(categories.map(c =>
            c.id === currentCategory.id ? currentCategory : c
          ));
        })
        .catch(error => {
          console.error('Error updating category:', error);
        });

    } else {
      // Create new category
      
      axios.post(`${process.env.REACT_APP_API_URL}/categories`, currentCategory, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })
        .then(response => {
          setCategories([...categories, response.data.data]);
        })
        .catch(error => {
          console.error('Error creating category:', error);
        });
    }

    handleCloseDialog();
  };

  const handleDeleteCategory = (id) => {
    // Check if category has questions
    const hasQuestions = questions.some(q => q.category_id === id);

    if (hasQuestions) {
      alert('Cannot delete category with associated questions. Please reassign or delete the questions first.');
      return;
    }

    // In a real application, we would send this request to the backend
    const updatedCategories = categories.filter(c => c.id !== id);
    setCategories(updatedCategories);

    // In a real application:
    axios.delete(`${process.env.REACT_APP_API_URL}/categories/${id}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    })
      .then(response => {
        setCategories(categories.filter(c => c.id !== id));
      })
      .catch(error => {
        console.error('Error deleting category:', error);
      });
  };

  const getQuestionsForCategory = (categoryId) => {
    return questions.filter(q => q.category_id === categoryId);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Category Manager
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Category
        </Button>
      </Box>

      <Grid container spacing={3}>
        {categories.map(category => {
          const categoryQuestions = getQuestionsForCategory(category.id);

          return (
            <Grid item xs={12} sm={6} md={4} key={category.id}>
              <Card>
                <CardContent>
                  <Typography variant="h6" component="h2" gutterBottom>
                    {category.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {category.description}
                  </Typography>
                  <Chip
                    icon={<QuestionIcon />}
                    label={`${categoryQuestions.length} Questions`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />

                  {categoryQuestions.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                        Questions in this category:
                      </Typography>
                      <List dense>
                        {categoryQuestions.slice(0, 3).map(question => (
                          <ListItem key={question.id} disablePadding>
                            <ListItemText primary={question.title} />
                          </ListItem>
                        ))}
                        {categoryQuestions.length > 3 && (
                          <ListItem disablePadding>
                            <ListItemText
                              primary={`+ ${categoryQuestions.length - 3} more questions`}
                              primaryTypographyProps={{
                                variant: 'body2',
                                color: 'text.secondary',
                                fontStyle: 'italic'
                              }}
                            />
                          </ListItem>
                        )}
                      </List>
                    </Box>
                  )}
                </CardContent>
                <CardActions>
                  <IconButton size="small" onClick={() => handleOpenDialog(category)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteCategory(category.id)}
                    disabled={categoryQuestions.length > 0}
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {currentCategory.id ? 'Edit Category' : 'Add New Category'}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            name="name"
            label="Category Name"
            value={currentCategory.name}
            onChange={handleInputChange}
            fullWidth
            required
            margin="normal"
          />
          <TextField
            name="description"
            label="Description"
            value={currentCategory.description}
            onChange={handleInputChange}
            fullWidth
            multiline
            rows={3}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSaveCategory}
            variant="contained"
            disabled={!currentCategory.name}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoryManager;
